import React, { useMemo } from 'react';
import classNames from 'classnames/bind';
import { Typography, Box } from '@mui/material';
import { conditionClassModifier } from '@surfline/web-common';

import styles from './RatingsTooltip.module.scss';

interface Props {
  condition: string;
  showNowLabel?: boolean;
  position?: 'left' | 'center' | 'right';
}

const cx = classNames.bind(styles);

const RatingsTooltip: React.FC<Props> = ({
  condition,
  showNowLabel = false,
  position = 'center',
}) => {
  const label = useMemo(() => (condition ? condition.replace(/_/g, ' ') : ''), [condition]);

  const classes = useMemo(() => {
    if (!condition) return { tooltipClass: '', colorClass: '', circleClass: '' };

    const conditionModifier = conditionClassModifier(condition);

    return {
      tooltipClass: cx({
        tooltip: true,
        [`tooltip${position?.charAt(0).toUpperCase()}${position?.slice(1)}`]: !!position,
      }),
      colorClass: `sl-conditions-color--${conditionModifier}-color`,
      circleClass: cx('tooltipCircle', `sl-conditions-color--${conditionModifier}-background`, {
        tooltipCircleLeft: position === 'left',
        tooltipCircleRight: position === 'right',
      }),
    };
  }, [condition, position]);

  if (!condition) return null;

  return (
    <Box className={classes.tooltipClass} data-testid="condition-ratings-tooltip">
      <Typography variant="overline2" className={`${classes.colorClass} ${styles.tooltipText}`}>
        {label}
      </Typography>
      {showNowLabel && (
        <Typography variant="overline2" className={styles.nowLabel}>
          Now
        </Typography>
      )}
      <div className={classes.circleClass} />
    </Box>
  );
};

export default RatingsTooltip;
